import { useState } from "react";
import toast from "react-hot-toast";
import { useRegisterMutation } from "@/redux/feature/auth/authApi";
import { handleApiRequest } from "@/utils/handleApiRequest";

import { CustomButton } from "@/components/ui/Buttons";
import GoogleIcon from "@/assets/icons/Google.svg";
import AuthInput from "../AuthInput";
import { Link, useSearchParams } from "react-router-dom";

interface CreateAccountV2Props {
  onNext: (email?: string) => void;
}

const CreateAccountV2 = ({ onNext }: CreateAccountV2Props) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [isLoading, setLoading] = useState(false);
  const [searchParams] = useSearchParams();

  const useExists = searchParams.get("useExists");

  const [register] = useRegisterMutation();

  const handleCreateAccount = async () => {
    if (!email || !password) {
      toast.error("Email and password are required");
      return;
    }

    if (password.length < 8) {
      toast.error("Password must be at least 8 characters");
      return;
    }

    try {
      setLoading(true);

      const payload = {
        email: email.trim().toLowerCase(),
        password,
        role: "creator",
      };

      await handleApiRequest({
        payload,
        requestFunction: register,
        onSuccess: () => {
          toast.success("Please check your email for verification");
          onNext(payload.email); // move to step 2 with the registered address
        },
        onError: (err) => {
          const error = err as {
            data?: { errorSource?: { message: string }[] };
          };

          if (error?.data?.errorSource?.length) {
            error.data.errorSource.forEach((item) => toast.error(item.message));
          } else {
            toast.error("Registration failed. Please try again.");
          }
        },
      });
    } catch (error) {
      toast.error("Something went wrong");
      console.error("Registration error:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSignIn = async () => {
    try {
      const api_baseUrl =
        import.meta.env.VITE_NODE_ENV === "development"
          ? import.meta.env.VITE_API_BASE_URL_LOCAL
          : import.meta.env.VITE_NODE_ENV === "stage"
            ? import.meta.env.VITE_API_BASE_URL_STAGE
            : import.meta.env.VITE_API_BASE_URL_PROD;

      const base_url = window.location.origin;

      const redirectUrl = `${window.location.origin}/auth/sign-up?step=2&googleVerify=true`;

      window.location.href = `${api_baseUrl}/auth/google-oauth?redirectTo=${encodeURIComponent(
        redirectUrl,
      )}&baseUrl=${encodeURIComponent(base_url)}&state=creator_register`;
    } catch (error) {
      toast.error("Google sign-in failed. Please try again.");
      console.error("Google sign-in error:", error);
    }
  };

  return (
    <div>
      {/* Header */}
      <div className="flex items-center gap-1 lg:gap-x-2 whitespace-nowrap">
        <div className="bg-[#FF6600] px-[8.5px] rounded-[20px]">
          <p className="text-white text-[11px] leading-5">Step 1</p>
        </div>
        <h1 className="text-[#060B13] text-base md:text-xl font-medium">
          Create. Get Discovered. Get{" "}
          <span className="text-[#FF6600]">Paid</span>.
        </h1>
      </div>

      <p className="text-[#475467] text-xs mt-2">
        Brands post real projects. You apply, create, and get paid—no outreach
        needed.
      </p>

      {/* Form */}
      <div className="mt-8 space-y-5">
        <AuthInput
          label="Email"
          name="email"
          type="email"
          placeholder="josephwilliams@gmail.com"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
        />

        <AuthInput
          label="Password"
          name="password"
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
        />
      </div>

      <p className="text-[#475467] text-xs mt-1.5">
        Must be at least 8 characters
      </p>

      <div className="mt-8 space-y-4">
        {useExists === "true" && (
          <p className="text-sm text-red-600 leading-[14px]">
            This account already exists. Please log in to continue.
          </p>
        )}
        <CustomButton
          backgroundColor="#FF6600"
          color="white"
          padding="10px"
          fontSize="16px"
          fontWeight="550"
          borderRadius="200px"
          className="w-full auth-btn-shadow"
          onClick={handleCreateAccount}
          disabled={isLoading}
        >
          {isLoading ? "Creating..." : "Create Account"}
        </CustomButton>

        <CustomButton
          variant="outline"
          color="#344054"
          padding="10px"
          fontSize="16px"
          fontWeight="550"
          borderRadius="200px"
          border="1px solid #D0D5DD"
          className="w-full flex justify-center items-center gap-x-3 auth-btn-shadow"
          onClick={handleGoogleSignIn}
          disabled={isLoading}
        >
          <GoogleIcon />
          <span>Sign Up with Google</span>
        </CustomButton>
      </div>
      <div className="flex items-center gap-x-1 mt-8 justify-center">
        <p className="text-[#475467] text-sm tracking-normal">
          Already have an account?
        </p>
        <Link
          to={"/auth/sign-in"}
          className="text-[#FF6600] text-sm tracking-normal font-semibold"
        >
          Login
        </Link>
      </div>
    </div>
  );
};

export default CreateAccountV2;
